import React, { useEffect } from "react";
import DebouncedTextInput from "../common/components/PersonalDataStep/DebouncedTextInput";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  PersonalData,
  PersonalDataValidation,
} from "../../../types/bookingCreation";
import { RootState } from "../../../redux/store";
import {
  emailChanged,
  firstNameChanged,
  lastNameChanged,
  setVoucherAmount,
  setVoucherCustomAmount,
  setVoucherSelectedAmount,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import { Box, Typography } from "@mui/material";
import VoucherAmountSelect from "../common/components/OrderDataStep/VoucherAmountSelect";
import DebouncedNumberInput from "../common/components/PersonalDataStep/DebouncedNumberInput";

const styles = {
  text: (theme: any) => ({
    textAlign: "left",
    width: "100%",
    paddingLeft: "25px",
    fontWeight: 400,
    color: theme.typography.primary,
    fontSize: "16px",
    marginBottom: "10px",
  }),
  termsOfServiceLink: (theme: any) => ({
    color: theme.palette.secondary.contrastText,
    textDecorationColor: theme.palette.secondary.contrastText,
    marginTop: "-6px",
  }),
  inputWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    margin: `12px auto 0 auto`,
  },
};

function OrderData() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const personalData: PersonalData = useSelector(
    (state: RootState) => state.bookingCreation.personalData
  );
  const personalDataValidation: PersonalDataValidation = useSelector(
    (state: RootState) => state.bookingCreation.personalDataValidation
  );

  const { voucherSelectedAmount, voucherCustomAmount } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );

  const amountData = ["10", "25", "50"];

  const onFirstNameChange = (firstName: string) =>
    dispatch(firstNameChanged({ firstName }));
  const onLastNameChange = (lastName: string) =>
    dispatch(lastNameChanged({ lastName }));
  const onCustomAmountChange = (amount: string) =>
    dispatch(setVoucherCustomAmount(amount));

  const onEmailChange = (email: string) => dispatch(emailChanged({ email }));

  //Hook to get the right voucher amount base on the selection user makes
  useEffect(() => {
    if (voucherSelectedAmount === t("general.voucher_purchase.custom_amount")) {
      dispatch(setVoucherAmount(Number(voucherCustomAmount)));
    } else {
      dispatch(setVoucherAmount(Number(voucherSelectedAmount)));
    }
  }, [voucherCustomAmount, voucherSelectedAmount, t, dispatch]);

  return (
    <div className="px-5">
      <Box sx={styles.inputWrapper}>
        <VoucherAmountSelect
          data={amountData}
          value={voucherSelectedAmount}
          onChange={(quantity: string) =>
            dispatch(setVoucherSelectedAmount(quantity))
          }
          disabled={false}
          label={t("general.voucher_purchase.amount_select")}
        />
      </Box>
      {voucherSelectedAmount ===
        t("general.voucher_purchase.custom_amount") && (
        <div className="my-4">
          <DebouncedNumberInput
            label={`${t("general.voucher_purchase.custom_amount_label")} (${
              tenantConfig.currencySymbol
            })`}
            initialValue={voucherCustomAmount}
            onChange={onCustomAmountChange}
            valid={personalDataValidation.firstName}
            autoComplete={"given-name"}
          />
        </div>
      )}

      <div className="my-4">
        <b className="font-semibold text-foreground">{`${t(
          "general.voucher_purchase.personal_data"
        )}`}</b>
      </div>
      <DebouncedTextInput
        label={t("book_location.personal_data.first_name")}
        initialValue={personalData.firstName}
        onChange={onFirstNameChange}
        valid={personalDataValidation.firstName}
        autoComplete={"given-name"}
      />
      <DebouncedTextInput
        label={t("book_location.personal_data.last_name")}
        initialValue={personalData.lastName}
        onChange={onLastNameChange}
        valid={personalDataValidation.lastName}
        autoComplete={"family-name"}
      />

      <DebouncedTextInput
        label={t("book_location.personal_data.email")}
        initialValue={personalData.email}
        onChange={onEmailChange}
        valid={personalDataValidation.email}
        autoComplete={"email"}
      />

      <div
        className="bg-[#EDF0F5] text-gray-800 p-2 rounded-lg mx-auto"
        style={{
          maxWidth: "100%",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <Typography>
          <Trans
            i18nKey={"general.voucher_purchase.note"}
            components={{
              bold: (
                <span
                  style={{
                    fontWeight: "bold",
                  }}
                ></span>
              ),
            }}
          />
        </Typography>
      </div>
    </div>
  );
}

export default OrderData;
